/**
 * Widget Demo Page - React Integration Examples
 *
 * Demonstrates various ways to integrate RefineVoiceWidget with different
 * use cases and configurations using React components.
 */

import { useRef, useState } from 'react';
import { RefineVoiceWidget, type RefineVoiceWidgetHandle } from '../components/RefineVoiceWidget';
import { refinementService } from '../services';
import './WidgetDemo.css';

export function WidgetDemo() {
  // State for external control demo
  const [isRecording, setIsRecording] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const externalWidgetRef = useRef<RefineVoiceWidgetHandle>(null);

  // State for custom prompt demo
  const [customPrompt, setCustomPrompt] = useState('Make this text more professional and formal');
  const [customPromptText, setCustomPromptText] = useState(
    'hey so like i was thinking maybe we could possibly meet up tomorrow or something to talk about the project stuff'
  );
  const [customPromptResult, setCustomPromptResult] = useState('');
  const [isRefiningCustom, setIsRefiningCustom] = useState(false);

  // Simple copy code functionality
  const copyCode = async (codeText: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const originalText = button.textContent;

    try {
      await navigator.clipboard.writeText(codeText);
      button.textContent = 'Copied!';
      button.classList.add('copied');

      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('copied');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = codeText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      button.textContent = 'Copied!';
      button.classList.add('copied');

      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('copied');
      }, 2000);
    }
  };

  // Handle custom prompt refinement
  const handleCustomRefine = async () => {
    if (!customPromptText.trim() || !customPrompt.trim()) return;

    setIsRefiningCustom(true);
    try {
      const result = await refinementService.refine(customPromptText, customPrompt);
      setCustomPromptResult(result.refinedText);
    } catch (error) {
      setCustomPromptResult(
        `Error: ${error instanceof Error ? error.message : 'Refinement failed'}`
      );
    } finally {
      setIsRefiningCustom(false);
    }
  };

  return (
    <div className="widget-demo-page">
      <header className="demo-header">
        <h1>RefineVoice Widget - React Integration</h1>
        <p className="demo-subtitle">
          Live examples showing different ways to integrate the RefineVoiceWidget component
        </p>
      </header>

      {/* Tab-like navigation between React, Web Component, and Vanilla JS */}
      <div className="demo-tabs">
        <a href="/widget/react" className="demo-tab active">
          React
        </a>
        <a href="/widget/webcomponent.html" className="demo-tab">
          Web Component
        </a>
        <a href="/widget/vanilla.html" className="demo-tab">
          Vanilla JS
        </a>
      </div>

      <div className="demo-nav">
        <a href="#basic">Basic</a>
        <a href="#interim">With Interim</a>
        <a href="#refine">Refine</a>
        <a href="#custom-prompt">Custom Prompt</a>
        <a href="#external">External Control</a>
      </div>

      {/* Section 1: Basic Textarea */}
      <section className="demo-section" id="basic">
        <h2>1. Basic Textarea</h2>
        <p>
          Simplest integration - just add the widget with a unique ID. Click the microphone to start
          recording.
        </p>

        <div className="code-example">
          <pre>{`<RefineVoiceWidget
  widgetId="basic-example"
  onTranscriptChange={(text) => console.log(text)}
/>`}</pre>
          <button
            className="copy-button green-theme"
            type="button"
            onClick={(e) =>
              copyCode(
                `<RefineVoiceWidget
  widgetId="basic-example"
  onTranscriptChange={(text) => console.log(text)}
/>`,
                e
              )
            }
          >
            Copy
          </button>
        </div>

        <div className="demo-container">
          <RefineVoiceWidget widgetId="basic-example" className="textarea-style" />
        </div>
      </section>

      {/* Section 2: Textarea with Interim Display */}
      <section className="demo-section" id="interim">
        <h2>2. Textarea with Interim Transcription</h2>
        <p>
          Shows real-time interim results as you speak. Gray text indicates interim (unconfirmed)
          transcription that updates as speech is recognized.
        </p>

        <div className="code-example">
          <pre>{`<RefineVoiceWidget
  widgetId="interim-example"
  showRecordingButton={true}
  onTranscriptChange={(text) => setTranscript(text)}
  onRecordingStateChange={(recording) => setIsRecording(recording)}
/>`}</pre>
          <button
            className="copy-button green-theme"
            type="button"
            onClick={(e) =>
              copyCode(
                `<RefineVoiceWidget
  widgetId="interim-example"
  showRecordingButton={true}
  onTranscriptChange={(text) => setTranscript(text)}
  onRecordingStateChange={(recording) => setIsRecording(recording)}
/>`,
                e
              )
            }
          >
            Copy
          </button>
        </div>

        <div className="demo-container demo-container-tall">
          <RefineVoiceWidget
            widgetId="interim-example"
            showRecordingButton={true}
            className="textarea-style"
          />
        </div>

        <div className="output-display">
          <strong>How it works:</strong>
          <ul>
            <li>Interim text appears in gray/italic as you speak</li>
            <li>Final confirmed text replaces interim when a phrase is complete</li>
            <li>Natural pauses trigger finalization</li>
          </ul>
        </div>
      </section>

      {/* Section 3: Refine Button */}
      <section className="demo-section" id="refine">
        <h2>3. With Refine Button</h2>
        <p>
          Enable the built-in refine button to polish text with AI. Speak or type, then click the
          sparkle button to refine.
        </p>

        <div className="code-example">
          <pre>{`<RefineVoiceWidget
  widgetId="refine-example"
  showRefineButton={true}
  refinePosition="bottom-right"
  defaultRefineStyle="professional"
  onRefine={(original, refined) => {
    console.log('Original:', original);
    console.log('Refined:', refined);
  }}
/>`}</pre>
          <button
            className="copy-button green-theme"
            type="button"
            onClick={(e) =>
              copyCode(
                `<RefineVoiceWidget
  widgetId="refine-example"
  showRefineButton={true}
  refinePosition="bottom-right"
  defaultRefineStyle="professional"
  onRefine={(original, refined) => {
    console.log('Original:', original);
    console.log('Refined:', refined);
  }}
/>`,
                e
              )
            }
          >
            Copy
          </button>
        </div>

        <div className="demo-container demo-container-tall">
          <RefineVoiceWidget
            widgetId="refine-example"
            showRefineButton={true}
            refinePosition="bottom-right"
            className="textarea-style"
          />
        </div>
      </section>

      {/* Section 4: Custom Prompt Refine */}
      <section className="demo-section" id="custom-prompt">
        <h2>4. Custom Prompt Refinement</h2>
        <p>
          Use your own prompt to control how text is refined. Perfect for specific use cases like
          translating to a language, changing tone, or applying custom formatting.
        </p>

        <div className="code-example">
          <pre>{`import { refinementService } from '../services';

// Refine with a custom prompt
const result = await refinementService.refine(
  text,
  'Translate this to Spanish and make it formal'
);
console.log(result.refinedText);`}</pre>
          <button
            className="copy-button green-theme"
            type="button"
            onClick={(e) =>
              copyCode(
                `import { refinementService } from '../services';

// Refine with a custom prompt
const result = await refinementService.refine(
  text,
  'Translate this to Spanish and make it formal'
);
console.log(result.refinedText);`,
                e
              )
            }
          >
            Copy
          </button>
        </div>

        <div className="custom-prompt-demo">
          <div className="form-group">
            <label className="form-label" htmlFor="custom-prompt-input">
              Your Custom Prompt:
            </label>
            <textarea
              id="custom-prompt-input"
              className="form-input custom-prompt-input"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Enter your refinement prompt..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="custom-prompt-text">
              Text to Refine:
            </label>
            <textarea
              id="custom-prompt-text"
              className="form-input"
              value={customPromptText}
              onChange={(e) => setCustomPromptText(e.target.value)}
              placeholder="Enter text to refine..."
              rows={3}
            />
          </div>

          <button
            type="button"
            className="external-btn refine-btn"
            onClick={handleCustomRefine}
            disabled={isRefiningCustom || !customPromptText.trim() || !customPrompt.trim()}
          >
            {isRefiningCustom ? 'Refining...' : 'Refine with Custom Prompt'}
          </button>

          {customPromptResult && (
            <div className="output-display">
              <strong>Result:</strong>
              <div className="output-box">{customPromptResult}</div>
            </div>
          )}
        </div>

        <div className="prompt-examples">
          <strong>Example Prompts:</strong>
          <div className="prompt-chips">
            <button
              type="button"
              className="prompt-chip"
              onClick={() => setCustomPrompt('Translate this to French')}
            >
              Translate to French
            </button>
            <button
              type="button"
              className="prompt-chip"
              onClick={() => setCustomPrompt('Make this sound like a pirate')}
            >
              Pirate speak
            </button>
            <button
              type="button"
              className="prompt-chip"
              onClick={() => setCustomPrompt('Summarize in one sentence')}
            >
              Summarize
            </button>
            <button
              type="button"
              className="prompt-chip"
              onClick={() => setCustomPrompt('Make this more concise and remove filler words')}
            >
              Concise
            </button>
            <button
              type="button"
              className="prompt-chip"
              onClick={() => setCustomPrompt('Rewrite as bullet points')}
            >
              Bullet points
            </button>
          </div>
        </div>
      </section>

      {/* Section 5: External Control */}
      <section className="demo-section" id="external">
        <h2>5. External Control via Ref</h2>
        <p>
          Control the widget programmatically using a ref. Useful for building custom UIs or
          integrating with other components.
        </p>

        <div className="code-example">
          <pre>{`const widgetRef = useRef<RefineVoiceWidgetHandle>(null);

// External control methods
widgetRef.current?.startRecording();
widgetRef.current?.stopRecording();
widgetRef.current?.toggleRecording();
widgetRef.current?.refine();
widgetRef.current?.getText();
widgetRef.current?.setText('New content');
widgetRef.current?.focus();

<RefineVoiceWidget
  ref={widgetRef}
  widgetId="external-control"
  showRecordingButton={false}  // Hide built-in button
  onTranscriptChange={setTranscribedText}
  onRecordingStateChange={setIsRecording}
/>`}</pre>
          <button
            className="copy-button green-theme"
            type="button"
            onClick={(e) =>
              copyCode(
                `const widgetRef = useRef<RefineVoiceWidgetHandle>(null);

// External control methods
widgetRef.current?.startRecording();
widgetRef.current?.stopRecording();
widgetRef.current?.toggleRecording();
widgetRef.current?.refine();
widgetRef.current?.getText();
widgetRef.current?.setText('New content');
widgetRef.current?.focus();

<RefineVoiceWidget
  ref={widgetRef}
  widgetId="external-control"
  showRecordingButton={false}
  onTranscriptChange={setTranscribedText}
  onRecordingStateChange={setIsRecording}
/>`,
                e
              )
            }
          >
            Copy
          </button>
        </div>

        <div className="demo-container">
          <RefineVoiceWidget
            ref={externalWidgetRef}
            widgetId="external-control"
            showRecordingButton={false}
            className="textarea-style"
            onTranscriptChange={setTranscribedText}
            onRecordingStateChange={setIsRecording}
          />
        </div>

        <div className="external-controls">
          <button
            type="button"
            className={`external-btn ${isRecording ? 'recording' : ''}`}
            onClick={() => externalWidgetRef.current?.toggleRecording()}
          >
            {isRecording ? 'Stop Recording' : 'Start Recording'}
          </button>
          <button
            type="button"
            className="external-btn refine-btn"
            onClick={() => externalWidgetRef.current?.refine()}
            disabled={!transcribedText.trim()}
          >
            Refine
          </button>
          <button
            type="button"
            className="external-btn"
            onClick={() => externalWidgetRef.current?.setText('')}
          >
            Clear
          </button>
          <button
            type="button"
            className="external-btn"
            onClick={() => externalWidgetRef.current?.focus()}
          >
            Focus
          </button>
        </div>

        <div className="output-display">
          <strong>Status:</strong> {isRecording ? 'Recording...' : 'Idle'}
          <br />
          <strong>Character count:</strong>{' '}
          <span className="char-count">{transcribedText.length}</span>
        </div>
      </section>

      {/* Footer with documentation link */}
      <footer className="demo-footer">
        <h3>Ready to integrate?</h3>
        <p>
          Check out <code>WIDGET_INTEGRATION.md</code> for complete API documentation
        </p>
        <div className="feature-badges">
          <span className="badge">Multi-Instance</span>
          <span className="badge">TypeScript</span>
          <span className="badge">Accessible</span>
          <span className="badge">Tested</span>
        </div>
      </footer>
    </div>
  );
}
